import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Tabs, Card, Descriptions, Tag, Button, Spin, message, Space } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { getAgreement } from '../api/preschools';
import useAuthStore from '../store/authStore';
import { formatMoney } from '../utils/money';
import PreschoolScheduleTab from '../components/preschool/PreschoolScheduleTab';
import PreschoolClassesTab from '../components/preschool/PreschoolClassesTab';
import PreschoolLessonsTab from '../components/preschool/PreschoolLessonsTab';
import PreschoolPaymentsTab from '../components/preschool/PreschoolPaymentsTab';

/**
 * Bir okulun bir SEZONA ait anlaşması. Modülün çalışma ekranı.
 *
 * Ödemeler sekmesi yalnızca `preschools.finance` yetkisiyle görünür —
 * backend de o uçları aynı izinle koruyor, buradaki gizleme ikinci katman
 * (arayüzde gizlemek yetki değildir).
 */
const PreschoolAgreementDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const finansVar = (user?.permissions || []).includes('preschools.finance');

  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  // Sekme KONTROLLU tutuluyor. Eskiden `loading` her yenilemede true
  // oluyordu ve asagidaki erken dönüş TÜM sekme bileşenini DOM'dan
  // kaldiriyordu; geri geldiginde AntD ic durumu sifirdan kuruluyor ve
  // kullanici hangi sekmedeyse ILK SEKMEYE atiliyordu. Her islemden sonra
  // yasaniyordu (Eren Bey bildirdi, 07.10.2026).
  const [aktifSekme, setAktifSekme] = useState('program');

  /**
   * @param {boolean} ilk  ilk yukleme mi - yalnizca o zaman tam sayfa
   *                       spinner gosteriliyor. Sonraki yenilemelerde
   *                       ekran YERINDE kaliyor.
   */
  const yukle = useCallback(async (ilk = false) => {
    if (ilk) setLoading(true);
    try {
      const { data } = await getAgreement(id);
      setAgreement(data);
    } catch {
      message.error('Anlaşma yüklenemedi.');
      navigate('/preschools');
    } finally {
      if (ilk) setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { yukle(true); }, [yukle]);

  if (loading) return <div className="py-16 text-center"><Spin /></div>;
  if (!agreement) return null;

  const okul   = agreement.preschool || {};
  const sezon  = agreement.season || {};
  const kesik  = !!agreement.end_date
    && sezon.end_date
    && dayjs(agreement.end_date).isBefore(dayjs(sezon.end_date));

  const items = [
    {
      key: 'program',
      label: 'Program',
      children: <PreschoolScheduleTab agreement={agreement} onChange={yukle} />,
    },
    {
      key: 'siniflar',
      label: 'Sınıflar ve Çocuklar',
      children: <PreschoolClassesTab agreement={agreement} onChange={yukle} />,
    },
    {
      key: 'dersler',
      label: 'Ders Günleri',
      children: <PreschoolLessonsTab agreement={agreement} />,
    },
    ...(finansVar ? [{
      key: 'odemeler',
      label: 'Ödemeler',
      children: <PreschoolPaymentsTab agreement={agreement} />,
    }] : []),
  ];

  return (
    <div>
      <Space className="mb-4">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/preschools')}>
          Anaokulu Takip
        </Button>
      </Space>

      <h2 className="text-2xl font-bold mb-1">{okul.name}</h2>
      <div className="text-gray-500 mb-4">
        {sezon.name}
        {kesik && <Tag color="orange" className="ml-2">Yarıda kesildi</Tag>}
      </div>

      <Card size="small" className="mb-4">
        <Descriptions size="small" column={{ xs: 1, sm: 2, md: 4 }}>
          <Descriptions.Item label="Yetkili">{okul.contact_name || '—'}</Descriptions.Item>
          <Descriptions.Item label="Telefon">{okul.contact_phone || '—'}</Descriptions.Item>
          <Descriptions.Item label="Konum">
            {[okul.district, okul.city].filter(Boolean).join(' / ') || '—'}
          </Descriptions.Item>
          <Descriptions.Item label="Sertifika">
            Sezonda {agreement.certificate_term_count} adet
          </Descriptions.Item>
          {finansVar && (
            <>
              <Descriptions.Item label="Aylık Tutar">
                {formatMoney(agreement.monthly_fee)}
              </Descriptions.Item>
              <Descriptions.Item label="Ödeme Günü">
                Her ayın {agreement.payment_day}. günü
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label="Aralık">
            {dayjs(agreement.start_date || sezon.start_date).format('DD.MM.YYYY')}
            {' — '}
            {dayjs(agreement.end_date || sezon.end_date).format('DD.MM.YYYY')}
          </Descriptions.Item>
          {agreement.end_reason && (
            <Descriptions.Item label="Bitiş Sebebi">{agreement.end_reason}</Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      {/* activeKey KONTROLLU - yenileme sonrasi bulunulan sekmede kalinsin */}
      <Tabs items={items} activeKey={aktifSekme} onChange={setAktifSekme} />
    </div>
  );
};

export default PreschoolAgreementDetail;
